import React, { useCallback, useMemo } from "react";
import {
  components,
  ClearIndicatorProps,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  MenuListProps,
  MultiValueGenericProps,
  OptionProps,
  NoticeProps
} from "react-select";
import { snakeCase } from "lodash";

type BaseOption = { label: string; [key: string]: any };

export const selectComponentsWithTestIds = <
  OptionType extends BaseOption = BaseOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>
>(
  testIdPrefix: string,
  isMenuDisabledCheck?: () => boolean,
  customDisabledOptionsMsg?: string
) => {
  const CustomControl = useCallback(
    (props: ControlProps<OptionType, IsMulti, Group>) => {
      const { innerProps, children, ...rest } = props;
      const enhancedInnerProps = {
        ...innerProps,
        "data-testid": `${testIdPrefix}_control`
      };
      return (
        <components.Control {...rest} innerProps={enhancedInnerProps}>
          {children}
        </components.Control>
      );
    },
    [testIdPrefix]
  );

  const CustomMenuList = useCallback(
    (props: MenuListProps<OptionType, IsMulti, Group>) => {
      const { innerProps, children, ...rest } = props;

      if (isMenuDisabledCheck?.() && !!customDisabledOptionsMsg) {
        const noticeProps = props as NoticeProps<OptionType, IsMulti, Group>;
        const { innerProps: noticeInnerProps, ...noticeRest } = noticeProps;
        const enhancedNoticeInnerProps = {
          ...noticeInnerProps,
          "data-testid": `${testIdPrefix}_disabled_list_message`
        };
        return (
          <components.NoOptionsMessage
            {...noticeRest}
            innerProps={enhancedNoticeInnerProps}
          >
            {customDisabledOptionsMsg}
          </components.NoOptionsMessage>
        );
      }

      const enhancedInnerProps = {
        ...innerProps,
        "data-testid": `${testIdPrefix}_menu_list`
      };
      return (
        <components.MenuList {...rest} innerProps={enhancedInnerProps}>
          {children}
        </components.MenuList>
      );
    },
    [testIdPrefix, isMenuDisabledCheck, customDisabledOptionsMsg]
  );

  const CustomDropdownIndicator = useCallback(
    (props: DropdownIndicatorProps<OptionType, IsMulti, Group>) => {
      const { innerProps, ...rest } = props;
      const enhancedInnerProps = {
        ...innerProps,
        "data-testid": `${testIdPrefix}_dropdown_indicator`
      };
      return (
        <components.DropdownIndicator
          {...rest}
          innerProps={enhancedInnerProps}
        />
      );
    },
    [testIdPrefix]
  );

  const CustomOption = useCallback(
    (props: OptionProps<OptionType, IsMulti, Group>) => {
      const { innerProps, children, ...rest } = props;
      const enhancedInnerProps = {
        ...innerProps,
        "data-testid": `${testIdPrefix}_item_${snakeCase(
          String(props.data.label ?? "")
        )}`
      };
      return (
        <components.Option {...rest} innerProps={enhancedInnerProps}>
          {children}
        </components.Option>
      );
    },
    [testIdPrefix]
  );

  const CustomClearIndicator = useCallback(
    (props: ClearIndicatorProps<OptionType, IsMulti, Group>) => {
      const { innerProps, children, ...rest } = props;
      const enhancedInnerProps = {
        ...innerProps,
        "data-testid": `${testIdPrefix}_clear_indicator`
      };
      return (
        <components.ClearIndicator {...rest} innerProps={enhancedInnerProps}>
          {children}
        </components.ClearIndicator>
      );
    },
    [testIdPrefix]
  );

  const CustomMultiValueLabel = useCallback(
    (props: MultiValueGenericProps<OptionType, IsMulti, Group>) => {
      const { innerProps, children, ...rest } = props;
      const enhancedInnerProps = {
        ...innerProps,
        title: String(props.data.label ?? ""),
        "data-testid": `${testIdPrefix}_value_${snakeCase(
          String(props.data.label ?? "")
        )}`
      };
      return (
        <components.MultiValueLabel {...rest} innerProps={enhancedInnerProps}>
          {children}
        </components.MultiValueLabel>
      );
    },
    [testIdPrefix]
  );

  return useMemo(
    () => ({
      Control: CustomControl,
      MenuList: CustomMenuList,
      DropdownIndicator: CustomDropdownIndicator,
      Option: CustomOption,
      ClearIndicator: CustomClearIndicator,
      MultiValueLabel: CustomMultiValueLabel
    }),
    [
      CustomControl,
      CustomMenuList,
      CustomDropdownIndicator,
      CustomOption,
      CustomClearIndicator,
      CustomMultiValueLabel
    ]
  );
};
